import {
  AssessmentStage,
  EditAssessmentArchivedArgs,
  EditPregnancyStagesArgs,
  PracticeAssessmentStage,
  StageHistoryOperation,
} from '@hb/shared'
import { useCallback, useContext, useMemo, useState } from 'react'

import { UseDisclosureReturn } from '@chakra-ui/react'
import { PopUpMessageContext, useApp, useMe } from '@hb/react'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../../../../backend/functions/functions'
import { useProfile } from '../../../../../contexts/user/ProfileContext'
import { useSelectedPractice } from '../../../../../hooks/backend/auth/useSelectedPractice'

// Custom hook for managing pregnancy status

const editStagesFunction = httpsCallable<EditPregnancyStagesArgs, { success: true }>(
  functions,
  'editPregnancyStages',
)
const editArchivedFunction = httpsCallable<EditAssessmentArchivedArgs, { success: true }>(
  functions,
  'editAssessmentArchived',
)

const getOperationText = (
  mode: StageHistoryOperation,
  stages: Array<AssessmentStage | PracticeAssessmentStage>,
) => {
  switch (mode) {
    case 'add':
      return `Added to ${stages.length} list(s)`
    case 'move':
      return `Moved to ${stages.length} list(s)`
    case 'remove':
      return `Removed from ${stages.length} list(s)`
    default:
      return ''
  }
}

export const usePregnancyStatus = <Stage extends AssessmentStage | PracticeAssessmentStage>(
  disclosure: UseDisclosureReturn,
) => {
  const { showError, showSuccess } = useContext(PopUpMessageContext)

  const { id: practiceId } = useSelectedPractice()
  const { selectedAssessment, assessmentId } = useProfile()
  const { appName } = useApp()
  const { adminRef, practiceRef, populated } = selectedAssessment ?? {}

  const stages = useMemo(() => {
    if (appName === 'app') return (populated?.adminStages ?? []) as Array<Stage>
    return (populated?.practiceStages ?? []) as Array<Stage>
  }, [appName, populated])

  const [hovered, setHovered] = useState<null | Stage>(null)
  const [mode, setMode] = useState<'add' | 'move' | null>(null)
  const [selectedIds, setSelectedIds] = useState<Array<Stage>>([])
  const [loadingIds, setLoadingIds] = useState<Array<Stage>>([])

  const handleCancel = useCallback(() => {
    setMode(null)
    setHovered(null)
    setSelectedIds([])
    setLoadingIds([])
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!mode || !assessmentId) return
    setLoadingIds([...selectedIds])
    try {
      await editStagesFunction({
        appName,
        assessmentId,
        practiceId,
        operation: mode,
        stages: selectedIds,
      })
      const statusText = getOperationText(mode, selectedIds)
      showSuccess(statusText)
      disclosure.onClose()
    } catch (err: any) {
      console.error(err)
      showError('Error updating assessment on lists', err?.message)
    }
    setSelectedIds([])
    setMode(null)
    setLoadingIds([])
  }, [mode, selectedIds, assessmentId, appName, showSuccess, showError, practiceId, disclosure])

  const handleItemClick = useCallback(
    async (id: Stage) => {
      const updateRef = appName === 'app' ? adminRef : practiceRef
      if (!updateRef) return
      if (!mode) {
        if (stages?.includes(id)) {
          if (stages.length < 2) {
            showError('Assessment must be on at least one list')
          } else {
            setLoadingIds([id])
            await editStagesFunction({
              appName,
              assessmentId: updateRef.id,
              practiceId,
              operation: 'remove',
              stages: [id],
            })
            showSuccess(`Removed from ${id}`)
            setLoadingIds([])
          }
        }
        return
      }
      if (selectedIds.includes(id)) {
        setSelectedIds(selectedIds.filter(i => i !== id))
      } else if (mode === 'add') setSelectedIds([...selectedIds, id])
      else setSelectedIds([id])
    },
    [mode, selectedIds, stages, adminRef, practiceRef, practiceId, appName, showError, showSuccess],
  )

  const setMoveMode = useCallback(() => {
    setMode('move')
    setSelectedIds(stages && Array.isArray(stages) ? stages : [])
  }, [stages])

  return {
    hovered,
    setHovered,
    mode,
    setMode,
    selectedIds,
    loadingIds,
    handleCancel,
    handleConfirm,
    handleItemClick,
    setMoveMode,
  }
}

export const useAssessmentArchived = (practiceStatus?: boolean) => {
  const { selectedAssessment, assessmentId } = useProfile()
  const { appName } = useApp()
  const me = useMe()
  const { practiceData, assessmentData, populated } = selectedAssessment ?? {}
  const { midwifeId } = populated ?? {}
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [loadingArchived, setLoadingArchived] = useState(false)
  const { showError, showSuccess } = useContext(PopUpMessageContext)

  const archivedBy = useMemo(
    () => (practiceStatus ? practiceData?.archivedBy : assessmentData?.archivedBy),
    [practiceStatus, assessmentData, practiceData],
  )

  const archivedOn = useMemo(
    () => (practiceStatus ? practiceData?.archivedOn : assessmentData?.archivedOn),
    [practiceStatus, assessmentData, practiceData],
  )

  const handleArchive = useCallback(
    (notes?: string) => {
      if (!me) return showError('No user')
      if (!assessmentId) return showError('No assessment ID')
      setLoadingArchived(true)

      return editArchivedFunction({
        appName,
        assessmentId,
        practiceId: midwifeId ?? null,
        archived: !archivedOn,
        notes,
      })
        .then(() => {
          showSuccess(`Pregnancy ${archivedOn ? 'un' : ''}archived`)
        })
        .catch((e: any) => {
          console.error(e)
          showError(
            `Error ${archivedOn ? 'un' : ''}archiving pregnancy${e?.message ? `: ${e.message}` : ''}`,
          )
        })
        .finally(() => setLoadingArchived(false))
    },
    [archivedOn, me, assessmentId, midwifeId, showError, showSuccess, appName],
  )

  return {
    archivedBy,
    archivedOn,
    handleArchive,
    loadingArchived,
    confirmArchive,
    setConfirmArchive,
  }
}
